import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUp, Bell, BellRing, Clock, Radio, Navigation, MapPin, CheckCircle2, Phone } from "lucide-react";
import { format } from "date-fns";
import { api } from "../lib/api";
import { STATUSES, PHONE_DISPLAY, PHONE_HREF } from "../lib/site";

const ICONS = { waiting: Clock, dispatched: Radio, "en-route": Navigation, "on-scene": MapPin, completed: CheckCircle2 };

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

export default function Track() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [alerts, setAlerts] = useState("idle");

  const enableAlerts = async () => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setAlerts("unsupported");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setAlerts("denied");
        return;
      }
      const reg = await navigator.serviceWorker.register("/sw.js");
      const { data: vapid } = await api.get("/push/vapid");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapid.public_key),
      });
      const json = sub.toJSON();
      await api.post("/push/subscribe", { request_id: id, endpoint: json.endpoint, keys: json.keys });
      setAlerts("on");
    } catch (e) {
      setAlerts("error");
    }
  };

  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .get(`/track/${id}`)
        .then(({ data: d }) => alive && setData(d))
        .catch(() => alive && setError(true));
    load();
    const t = setInterval(load, 10000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [id]);

  const currentIdx = data ? STATUSES.findIndex((s) => s.id === data.status) : -1;
  const historyAt = (sid) => (data?.status_history || []).find((h) => h.status === sid)?.at;

  return (
    <main className="min-h-screen bg-[#050505] flex flex-col" data-testid="track-page">
      <header className="border-b border-white/10">
        <div className="max-w-2xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="track-brand">
            <span className="w-8 h-8 bg-primary flex items-center justify-center">
              <ArrowUp size={18} strokeWidth={3} className="text-black" />
            </span>
            <span className="font-display font-bold uppercase tracking-wide text-sm">Tru North</span>
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Live Service Tracking</span>
        </div>
      </header>

      <div className="flex-1 max-w-2xl mx-auto w-full px-5 py-12">
        {error ? (
          <div className="border border-white/10 bg-[#121212] p-10 text-center" data-testid="track-not-found">
            <h1 className="font-display font-bold uppercase text-3xl">Reference not found.</h1>
            <p className="text-neutral-400 mt-3 text-sm">Double-check your tracking link, or call dispatch and we'll look it up.</p>
            <a href={PHONE_HREF} className="btn-primary mt-8">
              <Phone size={15} /> Call {PHONE_DISPLAY}
            </a>
          </div>
        ) : !data ? (
          <p className="text-neutral-500 font-mono text-xs text-center py-20">Loading your service status…</p>
        ) : (
          <>
            <div className="border border-white/10 bg-[#121212] p-6 md:p-8">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <h1 className="font-display font-bold uppercase tracking-normal text-2xl md:text-3xl" data-testid="track-service">
                  {data.service_label}
                </h1>
                <span className="font-mono text-xs text-neutral-500 uppercase tracking-[0.2em]">
                  Ref <span className="text-primary">{data.id.slice(0, 8).toUpperCase()}</span>
                </span>
              </div>
              <p className="mt-2 font-mono text-xs text-neutral-400 uppercase tracking-[0.15em]">
                {data.vehicle_year} {data.vehicle_make} {data.vehicle_model}
                <span className="mx-2">·</span>
                Quote <span className="text-primary">${data.quoted_price}</span>
                {data.price_note !== "flat rate" && <span> {data.price_note}</span>}
              </p>

              <div className="mt-8" data-testid="track-stepper">
                {STATUSES.map((s, i) => {
                  const Icon = ICONS[s.id];
                  const done = i < currentIdx;
                  const current = i === currentIdx;
                  const at = historyAt(s.id);
                  return (
                    <div key={s.id} className="flex gap-4" data-testid={`track-step-${s.id}`}>
                      <div className="flex flex-col items-center">
                        <span
                          className={`w-10 h-10 flex items-center justify-center border transition-colors duration-300 ${
                            current
                              ? "border-primary bg-primary text-black"
                              : done
                                ? "border-primary/50 text-primary"
                                : "border-white/15 text-neutral-600"
                          }`}
                        >
                          <Icon size={17} className={current ? "animate-pulse" : ""} />
                        </span>
                        {i < STATUSES.length - 1 && (
                          <span className={`w-px flex-1 min-h-6 ${i < currentIdx ? "bg-primary/50" : "bg-white/10"}`} />
                        )}
                      </div>
                      <div className="pb-7">
                        <p className={`font-display font-semibold uppercase tracking-wide ${current ? "text-primary" : done ? "text-neutral-200" : "text-neutral-600"}`}>
                          {s.label}
                          {current && <span className="ml-2 font-mono text-[9px] tracking-[0.2em] text-primary/70">● NOW</span>}
                        </p>
                        {at && (
                          <p className="font-mono text-[10px] text-neutral-500 mt-1">{format(new Date(at), "MMM d, h:mm a")}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              This page updates automatically as your technician moves
            </p>
            {alerts === "on" ? (
              <p data-testid="alerts-on" className="mt-3 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-400 flex items-center justify-center gap-2">
                <BellRing size={14} /> Status alerts on — we'll push every update to this device
              </p>
            ) : (
              <>
                <button data-testid="enable-alerts-btn" onClick={enableAlerts} className="btn-primary w-full mt-4">
                  <Bell size={15} /> Enable Push Status Alerts
                </button>
                {alerts === "denied" && (
                  <p className="mt-2 text-center font-mono text-[10px] text-red-400 uppercase tracking-[0.15em]">
                    Notifications are blocked — enable them in your browser settings
                  </p>
                )}
                {(alerts === "unsupported" || alerts === "error") && (
                  <p className="mt-2 text-center font-mono text-[10px] text-red-400 uppercase tracking-[0.15em]">
                    Push isn't available on this browser — keep this page open for live updates
                  </p>
                )}
              </>
            )}
            <a href={PHONE_HREF} data-testid="track-call-btn" className="btn-ghost w-full mt-6">
              <Phone size={15} /> Questions? Call {PHONE_DISPLAY}
            </a>
          </>
        )}
      </div>
    </main>
  );
}
